// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_skein/recap-stash
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Stash data stays local; only a DERIVED-SIGNALS
// object (usable yardage by weight, pattern surplus/shortfall, dye-lot risk, fiber mix,
// idle stash — see signals.rs) is sent for the recap. Raw rows never leave the device.
//
// HARDEST guardrail (REAL, pre-LLM here + mirrored server-side): the recap is
// OBSERVATIONAL + DETERMINISTIC. It describes what the user OWNS and where each queued
// pattern stands — it NEVER advises which yarn to buy, NEVER ranks a yarn "better", and
// NEVER values yarn for resale. Advice-shaped questions ("what should I buy?", "is X yarn
// good?", "what's this worth?") are REFUSED before any billed call — see isRefusedQuery.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { StashSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const APP_SLUG = "oriora_skein";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "refused"
  | "no_store"
  | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ── THE FENCE (pre-LLM, deterministic) ─────────────────────────────────────────
// The compliance ceiling is CAN-safe observational: describe the user's OWN stash +
// deterministic yardage math. The moment a request asks for ADVICE (buy/recommend/
// best/worth) it crosses into "advisory" territory the product may not occupy — so we
// REFUSE it here, before spending a billed call. This is a real gate, not UI copy:
// callers route any free-text query through `assertAllowed` first.
//
// Patterns are intentionally broad — false-positives (refusing a borderline phrasing)
// are the SAFE failure mode; a false-negative (billing an advice call) is not.
const ADVICE_PATTERNS: RegExp[] = [
  /\bshould i (buy|get|purchase|order|use|pick|choose)\b/i,
  /\bwhat (should|do you recommend|to) (i )?(buy|get|use|pick|knit|make|choose)\b/i,
  /\b(which|what) (yarn|brand|fiber|fibre|colou?r|colorway|weight) (is|are|would be) (best|better|good|right|nicer?|superior)\b/i,
  /\b(is|are) .* (yarn|brand|fiber|fibre|wool|cotton|acrylic) (good|bad|worth it|any good|better|the best)\b/i,
  /\brecommend( me)?\b/i,
  /\bwhat('?s| is) (it|this|that|my (stash|yarn)) worth\b/i,
  /\b(resale|resell|sell|market) (value|price|worth)\b/i,
  /\bhow much (is|are|could|would) .* (worth|sell for|go for)\b/i,
  /\bappraise|valuation|value (my|the|this) (stash|yarn)\b/i,
  /\bbest (yarn|brand|fiber|fibre|wool|value)\b/i,
  // quality judgement on a bare brand/colorway name — "is Malabrigo any good?", "is X worth it?"
  /\b(is|are|was|were)\b[^?.!]{0,40}\b(any good|worth (it|buying|the money)|the best|better than|good quality)\b/i,

  // ── RESALE/VALUATION, phrased the way people actually phrase it (added 2026-08-28) ──────────
  // The two patterns above it (`sell for|go for`, `(resale|sell) (value|price)`) only match when
  // the verb and its preposition are CONTIGUOUS. Real English puts the object in between —
  // "sell MY STASH for", "get FOR THIS YARN" — so they almost never fire. Measured live: 9 of 11
  // natural resale phrasings walked straight through the fence ("how much could I sell my stash
  // for?", "what would my stash go for?", "what should I charge for this yarn?"). Only "worth"
  // and "resale value" were caught, because those two happen to sit adjacent.
  // Skein has no selling surface at all, so any sell/resell intent is out of scope outright.
  /\b(sell|selling|sold|resell|reselling|resale)\b/i,
  /\bflip(ping)? (my|these|this|the)\b/i,
  /\b(how much|what) (could|would|can|might|will|do) i (get|make)\b/i,
  /\b(what|how much)\b[^?.!]{0,40}\bgo(ing)? for\b/i,
  /\bshould i (sell|price|charge|list)\b/i,
  /\bwhat should i (charge|price|list|ask)\b/i,
  /\bhow (do|should|would) i price\b/i,
  /\bprice (my|this|these|the) (stash|yarn|skeins?|collection)\b/i,
  /\b(is|are|was|were) (my|this|these|the)\b[^?.!]{0,40}\bvaluable\b/i,
  // NOTE the deliberate asymmetry: "cost", "spent" and "paid" are NOT here. Those are backward-
  // looking facts the user logged themselves (the `price` + `bought_on` fields), so "how much
  // have I spent on yarn?" is squarely observational and must stay ALLOWED. Only forward-looking
  // valuation of their yarn is fenced.
];

/** True if a free-text query asks for advice/valuation (must NOT reach a billed call). */
export function isRefusedQuery(q: string): boolean {
  const s = (q || "").trim();
  if (!s) return false;
  return ADVICE_PATTERNS.some((re) => re.test(s));
}

/** Refusal copy shown when a query is fenced out (no billed call was made). */
export const REFUSAL_MESSAGE =
  "Skein describes what you already own and does the yardage math for your queued patterns — it doesn't advise which yarn to buy, rank yarns as better, or value yarn for resale.";

/**
 * Gate any free-text query through the fence BEFORE a billed call. Throws a
 * BrainError("refused") — caught by the UI and shown as the refusal, never a charge.
 */
export function assertAllowed(query: string): void {
  if (isRefusedQuery(query)) {
    throw new BrainError("refused", REFUSAL_MESSAGE);
  }
}

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface StashRecap {
  recap: string;
}

/**
 * Stash recap — the ONE AI feature, ONE billed call per action (CRUD not agentic).
 *
 * Rust does the load-bearing math (signals.rs): usable yardage by weight (partial-skein
 * aware), pattern-vs-stash surplus/shortfall, dye-lot risk, fiber mix, idle stash. We send
 * ONLY those small aggregates; the brain NARRATES the figures in plain prose
 * ("You have 1,180 yds of fingering…; the Hawthorne cardigan you queued lists 1,400 yds —
 * about 220 yds short, roughly one more skein."). It stays OBSERVATIONAL — describes what's
 * owned + the deterministic gaps, never advises a purchase or values yarn.
 *
 * Endpoint: POST /api/apps/oriora_skein/recap-stash (Bearer JWT). Server prompt is an
 * operator task; it must read the `signals` object and hold the same refusal fence.
 */
export async function recapStash(signals: StashSignals): Promise<StashRecap> {
  if (signals.totals.skein_lines === 0) {
    throw new BrainError("unavailable", "Add a few skeins first — there's no stash to recap yet.");
  }
  const r = await brainCall<Partial<StashRecap>>("recap-stash", { signals });
  return { recap: String(r.recap ?? "").trim() };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_skein/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_skein/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile. Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must
// allowlist `oriora-skein` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
